// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.PermissionAccess.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import connect from 'share/enhancers/Izenda.Connect';
import actions from '../Izenda.PermissionActions';
import { isNil } from 'lodash/fp';

import EventsUtils from 'EventUtils';
import UserRolePermission from './Izenda.UserRolePermission';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import Input from 'share/components/controls/Izenda.Input';
import AccessGrid from 'modules/Access/Izenda.AccessGrid';
import { SECTION } from 'logic/data/Izenda.PermissionConstants';
import { ACCESS_MODE, PERMISSION_ACCESS_TABLE_DEF, PERMISSION_SEARCH_OPTIONS } from 'logic/data/Izenda.AccessConstants';

const UNCHECK_VALUE = 0;

@connect({ toClass: true }, actions)
export default class PermissionAccess extends Component {
  static propTypes = {
    permissionStore: PropTypes.object.isRequired,
    permission: PropTypes.object.isRequired
  };

  loadUsers = (pageInfo: any): any => {
    const { permissionStore } = this.props;
    return permissionStore.loadAvailableUsers(pageInfo);
  };

  loadRoles = (pageInfo: any): any => {
    const { permissionStore } = this.props;
    return permissionStore.loadAvailableRoles(pageInfo);
  };

  onSearchAccess = (searchKey: string) => {
    const { permissionStore } = this.props;
    permissionStore.loadPartialPermissionAccess(null, true, searchKey);
  };

  loadAccessLimits = (pageInfo: any) => {
    const { permissionStore } = this.props;
    permissionStore.loadPartialPermissionAccess(pageInfo);
  };

  updateSelectedNode = (node: any, parent: any, accessModel: any) => {
    const { permissionStore } = this.props;
    permissionStore.updateAccessLimitData(node, parent, accessModel);
  };

  render() {
    const notifyChange = EventsUtils.notifyChange;
    const { permissionStore, accessStore, permission, readOnly, localization } = this.props;
    const { tenantDetail } = permissionStore;
    const { access } = permission;

    return (
      !permission.fullReportAndDashboardAccess &&
      (isNil(tenantDetail) || tenantDetail.access.tenantAccess !== UNCHECK_VALUE) && (
        <div className={SECTION.Access}>
          <GridLayout className="izenda-GridLayout-Custom">
            <GridLayout.Row className="u_textBold u_paddingTop u_paddingBottom izenda-PermissionCategory-Header">
              <GridLayout.Col cols={11}>
                <a
                  className={this.props.getIconClass('isShowAccess')}
                  onClick={this.props.toggle.bind(this, 'isShowAccess')}
                />
                {localization.getText('COMMON_LABEL_ACCESS')}
              </GridLayout.Col>
              {permission.isTenantSetup && (
                <GridLayout.Col cols={1} className="u_textCenter">
                  <Input
                    type="checkbox"
                    readOnly={readOnly}
                    indeterminate={this.props.getIndeterminateCommon(access)}
                    checked={this.props.getCheckedCommon(access.tenantAccess)}
                    onChange={notifyChange(
                      permission.updateTenantAccessParent.bind(permission, access),
                      EventsUtils.CHECKBOX
                    )}
                  />
                </GridLayout.Col>
              )}
            </GridLayout.Row>
          </GridLayout>
          {permissionStore.isShowAccess && (
            <GridLayout>
              {(isNil(tenantDetail) || tenantDetail.access.accessLimits.tenantAccess !== UNCHECK_VALUE) && (
                <GridLayout.Row className="u_paddingTop u_paddingBottom u_borderBottom u_noMarginAll">
                  <GridLayout.Col cols={2} className="izenda-PermissionCategory-SubCategory">
                    {localization.getText('PERMISSION_LABEL_ACCESS_LIMITS')}
                  </GridLayout.Col>
                  <GridLayout.Col cols={9}>
                    <div className="u_inlineBlock u_fullWidth">
                      <UserRolePermission
                        {...this.props}
                        getData={this.loadAccessLimits}
                        searchRoles={this.onSearchAccess}
                        rolePermission={permissionStore.roleAccessLimits}
                        accessModel={accessStore.model}
                        updateSelectedNode={this.updateSelectedNode}
                      />
                    </div>
                  </GridLayout.Col>
                  {permission.isTenantSetup && (
                    <GridLayout.Col cols={1} className="u_textCenter">
                      <Input
                        type="checkbox"
                        readOnly={readOnly}
                        checked={this.props.getCheckedCommon(access.accessLimits.tenantAccess)}
                        onChange={notifyChange(
                          permission.updateTenantAccess.bind(permission, access, access.accessLimits),
                          EventsUtils.CHECKBOX
                        )}
                      />
                    </GridLayout.Col>
                  )}
                </GridLayout.Row>
              )}
              {(isNil(tenantDetail) || tenantDetail.access.accessDefaults.tenantAccess !== UNCHECK_VALUE) && (
                <GridLayout.Row className="u_paddingTop u_paddingBottom u_noMarginAll">
                  <GridLayout.Col cols={2} className="izenda-PermissionCategory-SubCategory">
                    {localization.getText('PERMISSION_LABEL_ACCESS_DEFAULT')}
                  </GridLayout.Col>
                  <GridLayout.Col cols={9}>
                    <div>
                      <AccessGrid
                        store={this.props.accessStore}
                        tableDef={PERMISSION_ACCESS_TABLE_DEF}
                        searchOptions={PERMISSION_SEARCH_OPTIONS}
                        accessMode={ACCESS_MODE.TENANT}
                        loadUsers={this.loadUsers}
                        loadRoles={this.loadRoles}
                        readOnly={readOnly}
                      />
                    </div>
                  </GridLayout.Col>
                  {permission.isTenantSetup && (
                    <GridLayout.Col cols={1} className="u_textCenter">
                      <Input
                        type="checkbox"
                        readOnly={this.props.readOnly}
                        checked={this.props.getCheckedCommon(access.accessDefaults.tenantAccess)}
                        onChange={notifyChange(
                          permission.updateTenantAccess.bind(permission, access, access.accessDefaults),
                          EventsUtils.CHECKBOX
                        )}
                      />
                    </GridLayout.Col>
                  )}
                </GridLayout.Row>
              )}
            </GridLayout>
          )}
        </div>
      )
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/Settings/PermissionTab/PermissionBody/Izenda.PermissionAccess.jsx